import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import emailjs from "@emailjs/browser";

import Logo from "../components/common/logo";
import Footer from "../components/common/footer";
import NavBar from "../components/common/navBar";
import Works from "../components/homepage/works";

import INFO from "../data/user";
import SEO from "../data/seo";

import "./styles/homepage.css";

import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, MessageCircle, Mic2, Users, Sparkles, Send, Bot, X, ShieldCheck } from "lucide-react";

const EMAILJS_SERVICE_ID = "service_jpezr0s";
const EMAILJS_TEMPLATE_ID = "template_o1mox8p";
const EMAILJS_PUBLIC_KEY = "Qc73pj33bbA0EKVxQ";

// 👇 Each slide has its own title + subtitle
const slideshowImages = [
	{
		src: "1.jpeg",
		title: "15 mil +",
		subtitle: "People Reached",
	},
	{
		src: "2.jpeg",
		title: "1 000 +",
		subtitle: "TALKS",
	},
	{
		src: "3.jpeg",
		title: "200+",
		subtitle: "Happy Clients",
	},
	{
		src: "4.jpeg",
		title: "175k +",
		subtitle: "Followers Online",
	},
	{
		src: "5.jpeg",
		title: "Global Leadership Coaching",
		subtitle: " Award Winner 2024",
	},
	{
		src: "6.jpeg",
		title: "Finalist",
		subtitle: "Inclusive Leader for Gender Mainstreaming Awards 2023",
	},
	{
		src: "7.jpeg",
		title: "Finalist",
		subtitle: "Inclusive Leader for Gender Mainstreaming Awards 2023",
	},
	{
		src: "9.jpeg",
		title: "Speaker & Coach",
		subtitle: "Inspiring people to grow",
	},
	{
		src: "10.jpeg",
		title: "Leadership Conversations",
		subtitle: "Helping people lead and thrive",
	},
	{
		src: "11.jpeg",
		title: "Media Contributor",
		subtitle: "Sharing ideas that make an impact",
	},
	{
		src: "12.jpeg",
		title: "Jabu Zwane",
		subtitle: "Speaker · Coach · Counsellor",
	},
];

const Homepage = () => {
	const navigate = useNavigate();
	const [adminAccessNoticeOpen, setAdminAccessNoticeOpen] = useState(false);
	const [questionOpen, setQuestionOpen] = useState(false);
	const [questionEmail, setQuestionEmail] = useState("");
	const [questionMessage, setQuestionMessage] = useState("");
	const [questionSending, setQuestionSending] = useState(false);
	const [questionSent, setQuestionSent] = useState(false);
	const [questionError, setQuestionError] = useState("");
	const [stayLogo, setStayLogo] = useState(false);
	const [logoSize, setLogoSize] = useState(100);
	const [oldLogoSize, setOldLogoSize] = useState(100);
	const [currentSlide, setCurrentSlide] = useState(0);
	const [companySlide, setCompanySlide] = useState(0);
	const [titleVisible, setTitleVisible] = useState(false);

	const COMPANIES = [
		{ name: "FNB", domain: "fnb.co.za" },
		{ name: "CRU", domain: "crugroup.com" },
		{ name: "MSX International", domain: "msxi.com" },
		{ name: "Motus", domain: "motuscorp.com" },
		{ name: "Volkswagen", domain: "volkswagen.com" },
		{ name: "WellSpan Health", domain: "wellspan.org" },
		{ name: "Tshwane University", domain: "tut.ac.za" },
		{ name: "Bidvest McCarthy", domain: "bidvestmccarthy.co.za" },
		{ name: "Standard Bank", domain: "standardbank.co.za" },
		{ name: "SARS", domain: "sars.gov.za" },
		{ name: "Capitec", domain: "capitecbank.co.za" },
		{ name: "Accenture", domain: "accenture.com" },
		{ name: "GIBS", domain: "gibs.co.za" },
		{ name: "Amka", domain: "amka.co.za" },
		{ name: "BMW", domain: "bmw.com" },
		{ name: "Nutun", domain: "nutun.com" },
		{ name: "ABB", domain: "abb.com" },
		{ name: "Transnet", domain: "transnet.net" },
	];

	useEffect(() => {
		window.scrollTo(0, 0);
		const timer = setTimeout(() => setTitleVisible(true), 100);
		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			let scroll = Math.round(window.pageYOffset, 2);
			let newLogoSize = 100 - (scroll * 4) / 10;
			if (newLogoSize < oldLogoSize) {
				if (newLogoSize > 40) {
					setLogoSize(newLogoSize);
					setOldLogoSize(newLogoSize);
					setStayLogo(false);
				} else {
					setStayLogo(true);
				}
			} else {
				setLogoSize(newLogoSize);
				setStayLogo(false);
			}
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [logoSize, oldLogoSize]);

	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentSlide((index) => (index + 1) % slideshowImages.length);
		}, 5000);
		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		const timer = setInterval(() => {
			setCompanySlide((index) => (index + 1) % COMPANIES.length);
		}, 2600);
		return () => clearInterval(timer);
	}, []);

	const goToSlide = useCallback((n) => {
		setCurrentSlide((n + slideshowImages.length) % slideshowImages.length);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "home");

	const openLiveChat = () => {
		window.dispatchEvent(new CustomEvent("jz:open-chat"));
		setQuestionOpen(false);
	};

	const sendQuestion = async (event) => {
		event.preventDefault();
		setQuestionSending(true);
		setQuestionError("");
		try {
			await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
				to_email: INFO.main.email,
				from_name: "Homepage question",
				from_email: questionEmail.trim(),
				reply_to: questionEmail.trim(),
				message: questionMessage.trim(),
				subject: "Website question from the homepage",
			}, EMAILJS_PUBLIC_KEY);
			setQuestionSent(true);
			setQuestionMessage("");
		} catch (error) {
			console.error("Question email failed:", error);
			setQuestionError("We could not send your question. Please try again or use live chat.");
		} finally {
			setQuestionSending(false);
		}
	};

	const logoStyle = {
		display: "flex",
		position: stayLogo ? "fixed" : "relative",
		top: stayLogo ? "3vh" : "auto",
		zIndex: 999,
		border: stayLogo ? "1px solid white" : "none",
		borderRadius: stayLogo ? "50%" : "none",
		boxShadow: stayLogo ? "0px 4px 10px rgba(0, 0, 0, 0.25)" : "none",
	};

	return (
		<React.Fragment>
			<Helmet>
				<title>{INFO.main.title}</title>
				<meta name="description" content={currentSEO.description} />
				<meta name="keywords" content={currentSEO.keywords.join(", ")} />
			</Helmet>

			<div className="page-content">
				<NavBar active="home" />
				<div className="content-wrapper">
					<div className="homepage-logo-container">
						<button
							type="button"
							className="homepage-admin-logo"
							onClick={() => setAdminAccessNoticeOpen(true)}
							aria-label="Open admin sign-in notice"
						>
							<div style={logoStyle}>
								<Logo width={logoSize} link={false} />
							</div>
						</button>
						<div className="homepage-profile-bubble">
							<span className="homepage-profile-bubble__eyebrow">Jabu Zwane</span>
							<strong>Helping people lead, grow and thrive.</strong>
							<span className="homepage-profile-bubble__roles">Counselling | Award Winning Speaker | Global Award Winning Leadership Coach | Media Contributor | Author</span>
						</div>
						<div className="homepage-company-strip">
							<span className="homepage-company-strip__label">Trusted by teams at</span>
							<div className="homepage-company-strip__viewport">
								<div className="homepage-company-strip__track" key={companySlide}>
									{[0, 1, 2, 3].map((offset) => {
										const company = COMPANIES[(companySlide + offset) % COMPANIES.length];
										return <span className="homepage-company-chip" key={company.name}>{company.name}</span>;
									})}
								</div>
							</div>
							<div className="homepage-company-strip__dots" aria-label="Company slideshow position">
								{COMPANIES.map((company, index) => <span key={company.name} className={index === companySlide ? "is-active" : ""} />)}
							</div>
						</div>

					</div>

					<div className="homepage-container">
						<div className="homepage-first-area">

							{/* ── Left side ── */}
							<div className="homepage-first-area-left-side">
								<div className="homepage-hero-kicker"><span aria-hidden="true" /> Speaker · coach · counsellor</div>
								<h1 className="homepage-title">
									Conversations that move people, teams and ideas forward.
								</h1>
								<div className="homepage-actions homepage-actions--headline" aria-label="Main actions">
									<Link className="homepage-action homepage-action--primary" to="/articles">
										<BookOpen size={17} aria-hidden="true" />
										<span>Explore books</span>
										<ArrowRight size={16} aria-hidden="true" />
									</Link>
									<Link className="homepage-action homepage-action--secondary" to="/contact">
										<MessageCircle size={17} aria-hidden="true" />
										<span>View services &amp; contact</span>
									</Link>
								</div>
								<div
									className="subtitle homepage-subtitle"
									style={{
										opacity: titleVisible ? 1 : 0,
										animation: titleVisible
											? "slideTextUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.18s both"
											: "none",
									}}
								>
									{INFO.homepage.description}
								</div>
								<div className="homepage-proof-points" aria-label="Professional highlights">
									<span><strong>15m+</strong> people reached</span>
									<span><strong>1,000+</strong> talks delivered</span>
									<span><strong>200+</strong> client partnerships</span>
								</div>
								<div className="homepage-question-actions">
									<button type="button" className="homepage-question-button" onClick={() => { setQuestionOpen(true); setQuestionSent(false); }}>
										<MessageCircle size={18} aria-hidden="true" /> Ask Jabu a question
									</button>
									<button type="button" className="homepage-chat-button" onClick={openLiveChat}>
										<Bot size={17} aria-hidden="true" /> Chat live
									</button>
								</div>
								<div className="homepage-service-menu" aria-label="Services">
									<Link to="/contact" className="homepage-service-menu__item"><Mic2 size={18} aria-hidden="true" /><span>Keynote speaking</span><ArrowRight size={15} aria-hidden="true" /></Link>
									<Link to="/contact" className="homepage-service-menu__item"><Users size={18} aria-hidden="true" /><span>Executive coaching</span><ArrowRight size={15} aria-hidden="true" /></Link>
									<Link to="/contact" className="homepage-service-menu__item"><Sparkles size={18} aria-hidden="true" /><span>Workshops &amp; mindset</span><ArrowRight size={15} aria-hidden="true" /></Link>
								</div>
							</div>
							{/* ── Right side: slideshow ── */}
							<div className="homepage-first-area-right-side">
								<div className="homepage-image-container">
									<div
										className="homepage-image-wrapper"
										role="group"
										aria-roledescription="carousel"
										aria-label="Highlights slideshow"
									>
										{slideshowImages.map((slide, i) => {
											const tileIndex = (i - currentSlide + slideshowImages.length) % slideshowImages.length;
											if (tileIndex >= 4) return null;
											return (
												<img
													key={`${slide.src}-${currentSlide}`}
													src={`/${slide.src}`}
													alt={`${slide.title} — ${slide.subtitle}`}
													className={`homepage-image homepage-image--tile-${tileIndex + 1}`}
												/>
											);
										})}

										{/* Dark gradient overlay for text readability */}
										<div
											className="homepage-image-collage__shade"
											style={{
												position: "absolute",
												inset: 0,
												background: "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.25) 45%, transparent 100%)",
												borderRadius: "inherit",
												pointerEvents: "none",
												zIndex: 1,
											}}
										/>

										<div
											key={`caption-${currentSlide}`}
											style={{
												position: "absolute",
												bottom: "52px",
												left: "22px",
												right: "22px",
												zIndex: 2,
												pointerEvents: "none",
											}}
										>
											{/* Animated accent line */}
											<div
												style={{
													height: "2px",
													background: "rgba(255,255,255,0.7)",
													marginBottom: "10px",
													borderRadius: "1px",
													animation: "lineGrow 0.5s cubic-bezier(0.4,0,0.2,1) 0.1s both",
												}}
											/>

											{/* Slide title */}
											<div
												style={{
													fontSize: "clamp(18px, 4vw, 26px)",
													fontWeight: 700,
													color: "#fff",
													lineHeight: 1.2,
													letterSpacing: "-0.01em",
													textShadow: "0 2px 12px rgba(0,0,0,0.5)",
													animation: "slideCaption 0.6s cubic-bezier(0.16,1,0.3,1) 0.2s both",
												}}
											>
												{slideshowImages[currentSlide].title}
											</div>

											{/* Slide subtitle */}
											<div
												style={{
													fontSize: "clamp(12px, 2vw, 14px)",
													fontWeight: 400,
													color: "rgba(255,255,255,0.72)",
													marginTop: "5px",
													letterSpacing: "0.04em",
													textTransform: "uppercase",
													textShadow: "0 1px 6px rgba(0,0,0,0.4)",
													animation: "slideCaptionSub 0.6s cubic-bezier(0.16,1,0.3,1) 0.35s both",
												}}
											>
												{slideshowImages[currentSlide].subtitle}
											</div>
										</div>

										<div
											key={`counter-${currentSlide}`}
											style={{
												position: "absolute",
												top: "14px",
												right: "16px",
												zIndex: 3,
												display: "flex",
												alignItems: "center",
												gap: "4px",
												animation: "slideTextLeft 0.5s ease both",
											}}
										>
											<span style={{
												fontSize: "18px",
												fontWeight: 700,
												color: "#fff",
												lineHeight: 1,
												textShadow: "0 2px 8px rgba(0,0,0,0.4)",
											}}>
												{String(currentSlide + 1).padStart(2, "0")}
											</span>
											<span style={{
												fontSize: "12px",
												color: "rgba(255,255,255,0.55)",
												alignSelf: "flex-end",
												marginBottom: "1px",
											}}>
												/ {String(slideshowImages.length).padStart(2, "0")}
											</span>
										</div>

										<div
											style={{
												position: "absolute",
												bottom: 0,
												left: 0,
												right: 0,
												height: "3px",
												background: "rgba(255,255,255,0.15)",
												zIndex: 3,
												borderRadius: "0 0 10% 10%",
												overflow: "hidden",
											}}
										>
											<div
												key={currentSlide}
												style={{
													height: "100%",
													background: "rgba(255,255,255,0.85)",
													animation: "progressLine 5s linear forwards",
												}}
											/>
										</div>

										<div
											style={{
												position: "absolute",
												bottom: "18px",
												left: "50%",
												transform: "translateX(-50%)",
												display: "flex",
												gap: "8px",
												zIndex: 3,
											}}
										>
											{slideshowImages.map((_, i) => (
												<button
													key={i}
													onClick={() => goToSlide(i)}
													aria-label={`Go to slide ${i + 1}`}
													style={{
														width: i === currentSlide ? "24px" : "8px",
														height: "8px",
														borderRadius: "4px",
														background: i === currentSlide
															? "rgba(255,255,255,0.95)"
															: "rgba(255,255,255,0.4)",
														border: "none",
														cursor: "pointer",
														padding: 0,
														transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
													}}
												/>
											))}
										</div>

										{[-1, 1].map((dir) => (
											<button
												key={dir}
												onClick={() => goToSlide(currentSlide + dir)}
												aria-label={dir === -1 ? "Previous" : "Next"}
												style={{
													position: "absolute",
													top: "50%",
													[dir === -1 ? "left" : "right"]: "12px",
													transform: "translateY(-50%)",
													background: "#17343c",
													border: "1px solid rgba(255,255,255,0.45)",
													color: "#fff",
													width: "36px",
													height: "36px",
													borderRadius: "50%",
													fontSize: "20px",
													lineHeight: 1,
													cursor: "pointer",
													zIndex: 3,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													transition: "background 0.2s, transform 0.2s",
												}}
											>
												{dir === -1 ? "‹" : "›"}
											</button>
										))}
									</div>
								</div>
							</div>
						</div>

						<div className="homepage-after-title">
							<div className="homepage-works">
								<Works />
							</div>
						</div>

						<div className="page-footer">
							<Footer />
						</div>
					</div>
				</div>

				{adminAccessNoticeOpen && (
					<div
						className="homepage-admin-notice-overlay"
						role="presentation"
						onMouseDown={(event) => event.target === event.currentTarget && setAdminAccessNoticeOpen(false)}
					>
						<div className="homepage-admin-notice" role="dialog" aria-modal="true" aria-labelledby="admin-notice-title">
							<div className="homepage-admin-notice__icon" aria-hidden="true"><ShieldCheck size={24} /></div>
							<div className="homepage-admin-notice__eyebrow">Restricted area</div>
							<h2 id="admin-notice-title">Administrator sign-in</h2>
							<p>This area is intended for authorised website administrators. If you are a customer, close this notice to return to the public website.</p>
							<div className="homepage-admin-notice__actions">
								<button type="button" className="homepage-admin-notice__cancel" onClick={() => setAdminAccessNoticeOpen(false)}>Cancel</button>
								<button
									type="button"
									className="homepage-admin-notice__continue"
									onClick={() => {
										setAdminAccessNoticeOpen(false);
										navigate("/admin");
									}}
								>
									Okay, continue
								</button>
							</div>
						</div>
					</div>
				)}

				{questionOpen && (
					<div className="homepage-question-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setQuestionOpen(false)}>
						<div className="homepage-question-modal" role="dialog" aria-modal="true" aria-labelledby="question-title">
							<button type="button" className="homepage-question-close" onClick={() => setQuestionOpen(false)} aria-label="Close question form"><X size={18} /></button>
							<div className="homepage-question-modal__eyebrow">Connect with Jabu</div>
							<h2 id="question-title">Ask a question</h2>
							<p>Send a message directly, or start a quick conversation with the live assistant.</p>
							{questionSent ? (
								<div className="homepage-question-success"><strong>Message sent.</strong><span>Thank you. Jabu's team will get back to you.</span><button type="button" onClick={() => setQuestionOpen(false)}>Close</button></div>
							) : (
								<form onSubmit={sendQuestion}>
									<label>Email address<input type="email" required value={questionEmail} onChange={(event) => setQuestionEmail(event.target.value)} placeholder="you@example.com" disabled={questionSending} /></label>
									<label>Your question<textarea required rows={5} value={questionMessage} onChange={(event) => setQuestionMessage(event.target.value)} placeholder="How can Jabu help you?" disabled={questionSending} /></label>
									{questionError && <div className="homepage-question-error">{questionError}</div>}
									<div className="homepage-question-modal__actions"><button type="button" className="homepage-chat-button" onClick={openLiveChat} disabled={questionSending}><Bot size={17} /> Chat live instead</button><button type="submit" className="homepage-question-button" disabled={questionSending}><Send size={16} /> {questionSending ? "Sending…" : "Send question"}</button></div>
								</form>
							)}
						</div>
					</div>
				)}
			</div>
		</React.Fragment>
	);
};

export default Homepage;